import { useMemo, useCallback, useEffect } from 'react';
import { useQuery, useReactiveVar } from '@apollo/client';
import UPCOMING_HOLIDAYS from '../graphql/queries/upcoming-holidays';
import { MAX_DATE_TO_PAY_DAYS, TODAY, MAX_DATE_TO_PAY } from '../constants/dates';
import { shouldDisableWeekend } from '../helpers/date-to-pay-restrictions';
import upcomingHolidayDatesVar from '../apollo/reactive-variables/upcoming-holidays';
import dayjs from '../config/dayjs';

export { MAX_DATE_TO_PAY_DAYS };

const isHolidayDate = (date, holidayDates) => (
  holidayDates.has(dayjs(date).startOf('day').format('YYYY-MM-DD'))
);

export const createIsDateDisabled = (holidayDates) => (date) => {
  const day = dayjs(date).startOf('day');
  return (
    day.isBefore(TODAY, 'day')
    || day.isAfter(MAX_DATE_TO_PAY, 'day')
    || shouldDisableWeekend(day)
    || isHolidayDate(day, holidayDates)
  );
};

export const useUpcomingHolidayDates = () => useReactiveVar(upcomingHolidayDatesVar);

export const useCartDateDisableCheck = () => {
  const holidayDates = useReactiveVar(upcomingHolidayDatesVar);
  return useMemo(
    () => (
      holidayDates.size > 0
        ? createIsDateDisabled(holidayDates)
        : shouldDisableWeekend
    ),
    [holidayDates],
  );
};

export const useUpcomingHolidays = (days = MAX_DATE_TO_PAY_DAYS) => {
  const { data, loading, error } = useQuery(UPCOMING_HOLIDAYS, {
    variables: { days },
    fetchPolicy: 'cache-first',
  });

  const holidayDates = useMemo(
    () => new Set(data?.upcomingHolidays?.map((h) => h.date) ?? []),
    [data],
  );

  useEffect(() => {
    upcomingHolidayDatesVar(holidayDates);
    return () => upcomingHolidayDatesVar(new Set());
  }, [holidayDates]);

  const isDateDisabled = useCallback(
    (date) => createIsDateDisabled(holidayDates)(date),
    [holidayDates],
  );

  return { holidayDates, isDateDisabled, loading, error };
};

export default useUpcomingHolidays;
